import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Settings, Users, Package, Boxes, ShoppingCart,
  CreditCard, Sparkles, Award, GitBranch, Network, Bell, Radio,
  FileText, Activity, ChevronsLeft, ChevronsRight, ShieldCheck,
  Banknote, Percent, Trophy, BarChart3, Gift, Image, X
} from "lucide-react";
import clsx from "clsx";

import { useUIStore } from "@/store/ui-store";

const SECTIONS = [
  {
    title: "Overview",
    items: [
      { to: "/admin",         label: "Dashboard",     icon: LayoutDashboard },
      { to: "/admin/health",  label: "System Health", icon: Activity        },
    ],
  },
  {
    title: "Commerce",
    items: [
      { to: "/admin/catalog",     label: "Catalog",     icon: Package     },
      { to: "/admin/inventory",   label: "Inventory",   icon: Boxes       },
      { to: "/admin/orders",      label: "Orders",      icon: ShoppingCart},
      { to: "/admin/payments",    label: "Payments",    icon: CreditCard  },
      { to: "/admin/membership",  label: "Membership",  icon: Sparkles    },
      { to: "/admin/storefront", label: "Storefront CMS", icon: Image },
    ],
  },
  {
    title: "Users & Finance",
    items: [
      { to: "/admin/users",         label: "Users",        icon: Users    },
      { to: "/admin/withdrawals",   label: "Withdrawals",  icon: Banknote },
      { to: "/admin/withdrawals/settings", label: "Withdraw Settings", icon: Settings },
      { to: "/admin/reward-rules",  label: "Reward Rules", icon: Award    },
      { to: "/admin/payment-gateways", label: "Payment Methods", icon: CreditCard },
    ],
  },
  {
    title: "Tree & Placement",
    items: [
      { to: "/admin/placement",     label: "Placement Queue", icon: GitBranch },
      { to: "/admin/tree",          label: "Tree Explorer",   icon: Network   },
    ],
  },
  {
    title: "Extensions",
    items: [
      { to: "/admin/referrals/config",      label: "Referral Config",  icon: Percent },
      { to: "/admin/referrals/commissions", label: "Commissions",      icon: Award },
      { to: "/admin/star/config",           label: "Star Config",      icon: Trophy },
      { to: "/admin/star/offers",           label: "Star Offers",      icon: Sparkles },
      { to: "/admin/agents",                label: "Agents",           icon: Users },
      { to: "/admin/reports",               label: "Reports",          icon: BarChart3 },
      { to: "/admin/special-offers",        label: "Special Offers",   icon: Gift },
    ],
  },
  {
    title: "Platform",
    items: [
      { to: "/admin/configuration", label: "Configuration",  icon: Settings   },
      { to: "/admin/notifications", label: "Notifications",  icon: Bell       },
      { to: "/admin/outbox",        label: "Outbox / Events",icon: Radio      },
      { to: "/admin/audit",         label: "Audit Log",      icon: FileText   },
    ],
  },
];

export function AdminSidebar() {
  const { sidebarCollapsed, toggleSidebar, mobileMenuOpen, closeMobileMenu } = useUIStore();

  return (
    <>
      {/* 📱 Backdrop Overlay (Mobile Only) */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-surface-900/60 backdrop-blur-sm lg:hidden transition-opacity" 
          onClick={closeMobileMenu} 
        />
      )}

      {/* 💻 Sidebar Container */}
      <aside className={clsx(
        // 🛠️ Mobile: Fixed & Sliding. Desktop: Sticky & Collapsible
        "fixed lg:sticky top-0 left-0 z-50 h-screen bg-surface-900 text-surface-100 flex flex-col transition-transform duration-300 ease-in-out shadow-2xl lg:shadow-none",
        mobileMenuOpen ? "translate-x-0 w-72" : "-translate-x-full lg:translate-x-0",
        !mobileMenuOpen && sidebarCollapsed ? "lg:w-16" : "lg:w-64"
      )}>
        
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-surface-800 shrink-0">
          <div className="flex items-center gap-2.5 overflow-hidden">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 grid place-items-center shrink-0">
              <ShieldCheck className="h-5 w-5 text-white" />
            </div>
            <div className={clsx("transition-opacity", (!mobileMenuOpen && sidebarCollapsed) ? "lg:hidden" : "block")}>
              <div className="text-sm font-bold leading-tight truncate">Admin Console</div>
              <div className="text-[10px] text-surface-400 font-mono">v5.0</div>
            </div>
          </div>

          {/* Mobile Close Button */}
          <button onClick={closeMobileMenu} className="lg:hidden p-1.5 rounded-md text-surface-400 hover:bg-surface-800 hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
          
          {/* Desktop Collapse Button */}
          <button onClick={toggleSidebar} className="hidden lg:block p-1.5 rounded-md text-surface-400 hover:bg-surface-800 hover:text-white transition-colors">
            {sidebarCollapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-5 custom-scrollbar">
          {SECTIONS.map((section) => (
            <div key={section.title}>
              <div className={clsx(
                "px-3 mb-1.5 text-[10px] font-bold uppercase tracking-widest text-surface-500",
                (!mobileMenuOpen && sidebarCollapsed) && "lg:hidden"
              )}>
                {section.title}
              </div>
              
              <div className="space-y-1">
                {section.items.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to} to={to} end={to === "/admin"}
                    onClick={closeMobileMenu} // 🛠️ Closes drawer on click in mobile
                    className={({ isActive }) => clsx(
                      "flex items-center gap-3 rounded-lg text-sm font-medium transition-all group",
                      (!mobileMenuOpen && sidebarCollapsed) ? "px-0 justify-center h-10 w-10 mx-auto" : "px-3 py-2",
                      isActive
                        ? "bg-brand-600 text-white shadow-md shadow-brand-500/20"
                        : "text-surface-400 hover:bg-surface-800 hover:text-white",
                    )}
                  >
                    <Icon className="h-4.5 w-4.5 shrink-0" />
                    <span className={clsx("truncate", (!mobileMenuOpen && sidebarCollapsed) ? "lg:hidden" : "block")}>
                      {label}
                    </span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}